// #POW-9
import Config from '../../Config';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton, CircularProgress, Box, Card, Typography, CardContent, Stack, Chip, Divider, useMediaQuery } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import StoreIcon from '@mui/icons-material/Store';
import PersonIcon from '@mui/icons-material/Person';
import { useEffect, useState } from 'react';
import useCustomFetch from '../../hooks/useCustomFetch';
import { TransactionDetail, Event, ShippingData, CheckInData } from '../../types/Types'; // #POW-13
import { DataGrid, GridColDef, GridRowsProp, GridToolbarQuickFilter } from '@mui/x-data-grid';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'; // #POW-15
import FileDownloadIcon from '@mui/icons-material/FileDownload'; // #POW-16
import { blue } from '@mui/material/colors'; // #POW-23
import { useTranslation } from "react-i18next"; // #POW-29

function formattedDate(date: string | Date) { 
    return new Intl.DateTimeFormat('it-IT', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }).format(new Date(date));
  };

function QuickSearchToolbar() {
    return (
        <Box
            sx={{
                p: 0.5,
                pb: 0,
                textAlign: 'right',
                width: '100%'
            }}
        >
            <GridToolbarQuickFilter sx={{
                width: '100%'
            }}/>
        </Box>
    );
}

export default function DialogDetailTransaction(
    { open, onClose, transactionNumber, shippingData, checkInData }: { open: boolean; onClose: () => void; transactionNumber?: string; shippingData?:ShippingData; checkInData?:CheckInData  }
){
    const { t } = useTranslation();  // #POW-29
    const customFetch = useCustomFetch()
    const isSm = useMediaQuery((theme) => theme.breakpoints.down('sm'));
    const [downloadExcelLoading, setDownloadExcelLoading] = useState(false);  // #POW-23
    const [downloadPdfLoading, setDownloadPdfLoading] = useState(false);  // #POW-23
    const [isLoading, setIsLoading] = useState(false);
    const [hasDiagnosticData, setHasDiagnosticData] = useState(false); // #POW-16

    const [datasource, setDatasource] = useState<TransactionDetail|null>(null)

    const [rows, setRows] = useState<GridRowsProp>([])
    let events: Event[] = [];

    const columns: GridColDef[] = [
        { field: 'title', headerName: t("transacionDetail.eventsTable.title"), minWidth: 200, flex: 0.5 },
        { field: 'description', headerName: t("transacionDetail.eventsTable.description"), minWidth: 200, flex: 1 },
        {
            field: 'date', headerName: t("transacionDetail.eventsTable.date"), minWidth: 200, flex: 0.5,
            renderCell: (params) => formattedDate(params.value)
        },
    ];

    useEffect(() => {
        if (open) {
            fetchData()
        }
    }, [open]);
    
    const handleDownloadPdf = async () => { // #POW-15
        setDownloadPdfLoading(true);  // #POW-23
        const url = `${Config.serverUrl}/api/trade-in/transaction/${transactionNumber}/pdf`;
        const response = await customFetch(url, {
            method: 'GET',
            headers: {
            'Content-Type': 'application/pdf',
            },
        });

        if (response.ok) {
            const blob = await response.blob();
            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.download = `transaction_${transactionNumber}.pdf`;
            link.click();
            setDownloadPdfLoading(false); // #POW-23
        } else {
            console.error('Failed to download PDF');
        }
    };

    // #POW-16 Download excel file with diagnostic data
    const handleDownloadDiagnosticDataExcel = async() => { 
        setDownloadExcelLoading(true); // #POW-23
        const url = `${Config.serverUrl}/api/trade-in/transaction/${transactionNumber}/diagnostic-data/excel`;
        const response = await customFetch(url, {
            method: 'GET',
            headers: {
            'Content-Type': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            },
        });

        if (response.ok) {
            const blob = await response.blob();
            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.download = `diagnostic_data_${transactionNumber}.xlsx`;
            link.click();
            setDownloadExcelLoading(false); // #POW-23
        } else {
            console.error('Failed to download excel file');
        }
    }

    const fetchData = async () => {
        setIsLoading(true)
        if(transactionNumber)
        {
            const url = `${Config.serverUrl}/api/picea_online/transactions/${transactionNumber}` // #POW-8
            const response = await customFetch(url,
                {
                method: "GET",
                headers: {},
                })
            const data = await response.json()
            //console.log('response', data)
            if(data.detail && data.detail == 'Transaction not found') // #POW-81
            {
                setIsLoading(false)
                setDatasource(null)
                setRows([])
                setHasDiagnosticData(false)
                return
            }

            events = data.events.map((item: Event, index: number) => ({
                id: index + 1,
                title: item.title,
                description: item.description,
                date: item.date,
            }));

            const hasDiagnosticData = events.some((event) => {  // #POW-16
                return event.title === "Diagnóstico";
            });

            setHasDiagnosticData(hasDiagnosticData); // #POW-16
            
            // #POW-18 get all custom fields
            const customFields = data.customFields.map((item: any) => ({ 
                key: item.key,
                value: item.value
            }))

            setRows(events)
            
            const transactionDetail: TransactionDetail = {
                number: data.number,
                completed: data.completed,
                price: data.price,
                currency: data.currency,
                grade: data.grade,
                date: new Date(data.date),
                device: {
                    manufacturer: data.device.manufacturer,
                    name: data.device.name,
                    storage: data.device.storage,
                    imei: data.device.imei,
                    serialNumber: data.device.sn,
                    model: data.device.model_code
                },
                shop: {
                    company: data.shop.company,
                    name: data.shop.name,
                    employee: data.shop.employee
                },
                events: events,
                logisticCustomSkuTransfer: customFields.find((item: any) => item.key === "CUSTOM_SKU_Transfer")?.value || ""
            };
    
            setDatasource(transactionDetail);
            
            setIsLoading(false)
        }
        
    }

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth='lg'>
            <DialogTitle fontWeight={'bold'}> {t('transacionDetail.title', { value: transactionNumber })}</DialogTitle>
            <IconButton
                aria-label="close"
                onClick={onClose}
                sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                }}
            >
                <CloseIcon />
            </IconButton>
            <DialogContent dividers>
                {/* Render selectedRow details here */}
                {isLoading && (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <CircularProgress />
                </div>
                )}
                {!isLoading && (
                    <>
                        <Stack spacing={{xs:1, sm:2}} direction={{ xs: "column", sm: "row" }} sx={{m: 1.5, justifyContent: "flex-end"}}> {/* #POW-15 */}
                            <Box sx={{ m: 1, position: 'relative' }}> {/* #POW-23 */}
                                <Button variant="contained" 
                                    startIcon={<PictureAsPdfIcon />} 
                                    onClick={() => {handleDownloadPdf();}} 
                                    disabled={downloadPdfLoading} // #POW-23
                                    fullWidth={isSm}
                                >
                                    {t("transacionDetail.export")}
                                </Button>
                                {downloadPdfLoading && ( // #POW-23
                                    <CircularProgress
                                        size={24}
                                        sx={{
                                        color: blue[900],
                                        position: 'absolute',
                                        top: '50%',
                                        left: '50%',
                                        marginTop: '-12px',
                                        marginLeft: '-12px',
                                        }}
                                    />
                                )}
                            </Box>
                            <Divider orientation="vertical" flexItem />
                            <Box sx={{ m: 1, position: 'relative' }}> {/* #POW-23 */}
                                <Button variant="contained" 
                                    startIcon={<FileDownloadIcon />} 
                                    onClick={() => {handleDownloadDiagnosticDataExcel();}}
                                    disabled={!hasDiagnosticData || downloadExcelLoading} // #POW-23
                                    fullWidth={isSm}
                                    > {/* #POW-16 */}
                                    {t("transacionDetail.exportDiagnosticData")}
                                </Button>
                                {downloadExcelLoading && ( // #POW-23
                                    <CircularProgress
                                        size={24}
                                        sx={{
                                        color: blue[900],
                                        position: 'absolute',
                                        top: '50%',
                                        left: '50%',
                                        marginTop: '-12px',
                                        marginLeft: '-12px',
                                        }}
                                    />
                                )}
                            </Box>
                        </Stack>
                        <Card variant="outlined" sx={{ m: 1.5, p: 1.5, backgroundColor:'#f5f5f5' }}>
                            <CardContent>
                                <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{justifyContent: "space-between"}}>
                                    <Box maxWidth={'500px'}>
                                        <Typography sx={{ mb: 1 }} fontWeight={'bold'}>
                                            {t("transacionDetail.transactionNumber")}: {transactionNumber}
                                        </Typography>
                                        <Typography sx={{ mb: 1 }}> {/* #POW-18 */}
                                            {t("transacionDetail.skuTransfer")}: {datasource?.logisticCustomSkuTransfer}
                                        </Typography>
                                        <Typography sx={{ mb: 1 }}>
                                            {t("transacionDetail.date")}: {datasource?.date && formattedDate(datasource?.date)}
                                        </Typography>
                                        <Typography sx={{ mb: 1 }}>
                                            {datasource?.device?.manufacturer} {datasource?.device.name}
                                        </Typography>
                                    </Box>
                                    
                                    {shippingData && // #POW-13
                                        <Box borderTop={{xs:"1px solid #cccccc", sm: "none"}} paddingTop={{xs: "10px", sm:"0px"}}>
                                            <Typography sx={{ mb: 1 }}>
                                                <b>{t("transacionDetail.shippingCode")}:</b> {datasource && shippingData.shippingCode} {/* #POW-81 */}
                                            </Typography>
                                            <Typography sx={{ mb: 1 }}>
                                                <b>{t("transacionDetail.shippingDate")}:</b> {datasource && formattedDate(shippingData.shippingDate)} {/* #POW-81 */}
                                            </Typography>
                                            {checkInData && // #POW-21 
                                                <>
                                                    <Divider sx={{marginBottom:"10px"}}/>
                                                    <Typography sx={{ mb: 1 }} >
                                                        <b>{t("transacionDetail.checkInDate")}:</b> {datasource && formattedDate(checkInData.checkInDate)} {/* #POW-81 */}
                                                    </Typography>
                                                </>
                                            }
                                        </Box>
                                    }
                                    
                                    <Box textAlign={{ xs: "left", sm: "right" }} borderTop={{xs:"1px solid #cccccc", sm: "none"}} paddingTop={{xs: "10px", sm:"0px"}}>
                                        <Typography sx={{ mb: 1 }} fontWeight={'bold'}>
                                            {t("transacionDetail.price")}: {datasource?.price} {datasource?.currency == "EUR" ? "€" : datasource?.currency}
                                        </Typography>
                                        {datasource?.completed && datasource.completed == true ? 
                                            <Chip label={t("transacionDetail.completed")} color={'success'} size="small" sx={{ mb: 1 }}/> : 
                                            <Chip label={t("transacionDetail.notCompleted")} color={'default'} size="small" sx={{ mb: 1 }}/>
                                        }
                                    </Box>
                                </Stack>
                            </CardContent>
                        </Card>
                        <Stack direction={{ sm: "column", md: "row" }} spacing={2} sx={{justifyContent: "space-between", m: 1.5}}>
                            <Card variant="outlined" sx={{ p: 1.5, backgroundColor:'#f5f5f5', flex:2 }}> {/* Device data */}
                                <CardContent>
                                    <Box>
                                        <Typography sx={{ mb: 1 }} fontWeight={'bold'}>
                                            {t("transacionDetail.device")}: {datasource?.device?.manufacturer} {datasource?.device?.name}
                                        </Typography>
                                        <Typography sx={{ mb: 1 }}>
                                            {t("transacionDetail.storage")}: {datasource?.device?.storage}
                                        </Typography>
                                        <Typography sx={{ mb: 1 }}>
                                            {t("transacionDetail.model")}: {datasource?.device?.model}
                                        </Typography>
                                        <Typography sx={{ mb: 1 }}>
                                            {t("transacionDetail.imei")}: {datasource?.device.imei}
                                        </Typography>
                                        <Typography sx={{ mb: 1 }}>
                                            {t("transacionDetail.serialNumber")}: {datasource?.device.serialNumber}
                                        </Typography>
                                    </Box>
                                </CardContent>
                            </Card>
                            <Box sx={{ p: 1.5, flex:1 }}> {/* Shop data */}
                                <Stack direction="row" spacing={1} alignItems="center" mb={2}>
                                    <StoreIcon />
                                    <Typography sx={{ mb: 1 }} fontWeight="bold">
                                        {t("transacionDetail.shop")}
                                    </Typography>
                                </Stack>
                                <Typography sx={{ mb: 1 }}>
                                    {t("transacionDetail.company")}: {datasource?.shop.company}
                                </Typography>
                                <Typography sx={{ mb: 1 }}>
                                    {t("transacionDetail.shop")}: {datasource?.shop.name}
                                </Typography>
                            </Box>
                            <Box sx={{ p: 1.5, flex:1 }}> {/* Operator data */}
                                <Stack direction="row" spacing={1} alignItems="center" mb={2}>
                                    <PersonIcon />
                                    <Typography sx={{ mb: 1 }} fontWeight="bold">
                                        {t("transacionDetail.operator")}
                                    </Typography>
                                </Stack>
                                <Typography sx={{ mb: 1 }}>
                                    {t("transacionDetail.operatorName")}: {datasource?.shop.employee}
                                </Typography>
                            </Box>
                            
                        </Stack>

                        <Box sx={{ p: 1.5 }}> {/* Operation history*/}
                            <Typography sx={{ mb: 1 }} fontWeight={'bold'} width={"100%"} borderBottom={"1px solid #cccccc"}>
                                {t("transacionDetail.operationHistory")}
                            </Typography>
                            <Box sx={{ width: '100%', maxWidth: { sm: '100%' }, height: '100%' }}>
                                <DataGrid
                                    autoHeight
                                    disableRowSelectionOnClick
                                    rows={rows}
                                    columns={columns}
                                    getRowId={(row) => row.id}
                                    getRowClassName={(params) =>
                                        params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
                                    }
                                    initialState={{
                                        pagination: { paginationModel: { pageSize: 10 } },
                                    }}
                                    pageSizeOptions={[10, 20, 50, 100]}
                                    disableColumnResize
                                    density="compact"
                                    slots={{ toolbar: QuickSearchToolbar }}
                                />
                            </Box>
                        </Box>
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="primary" variant="contained">
                    {t("transacionDetail.close")}
                </Button>
            </DialogActions>
        </Dialog>
    );
}


